import { create } from 'zustand';
import { createId } from '../lib/ids';
import { duplicateMarkupElement, normalizeAutomaticNumbers } from '../lib/element-operations';
import { createProjectAssets, createProjectFromFile, refreshProjectThumbnail } from '../services/image-engine';
import { projectStore } from '../services/project-store';
import {
  getMarkupLayer,
  replaceMarkupElements,
  type ImageElement,
  type MarkupElement,
  type ProjectAsset,
  type RealImagerProject
} from '../types/project';

export type AppRoute = 'home' | 'editor';
export type SaveStatus = 'saved' | 'saving' | 'error';

interface Notice {
  kind: 'success' | 'error' | 'info';
  message: string;
}

interface AppState {
  route: AppRoute;
  projects: RealImagerProject[];
  currentProject: RealImagerProject | null;
  history: RealImagerProject[];
  future: RealImagerProject[];
  loading: boolean;
  dirty: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;
  notice: Notice | null;
  setRoute(route: AppRoute): void;
  clearNotice(): void;
  loadProjects(): Promise<void>;
  createProject(file: File): Promise<void>;
  openIncomingFiles(files: File[]): Promise<void>;
  openProject(id: string): Promise<void>;
  closeProject(): Promise<void>;
  updateProject(updater: (project: RealImagerProject) => RealImagerProject, recordHistory?: boolean): void;
  saveProject(showNotice?: boolean): Promise<void>;
  renameProject(id: string, name: string): Promise<void>;
  duplicateProject(id: string): Promise<void>;
  deleteProject(id: string): Promise<void>;
  importProject(file: File): Promise<void>;
  exportProject(id: string): Promise<Blob>;
  undo(): void;
  redo(): void;
  updateElement(id: string, updater: (element: MarkupElement) => MarkupElement, recordHistory?: boolean): void;
  addImageElement(file: File): Promise<string | null>;
  replaceImageElement(id: string, file: File): Promise<string | null>;
  renameElement(id: string, name: string): void;
  setElementVisibility(id: string, visible: boolean): void;
  setElementLocked(id: string, locked: boolean): void;
  moveElement(id: string, direction: -1 | 1): void;
  reorderElement(id: string, destination: 'front' | 'forward' | 'backward' | 'back'): void;
  duplicateElement(id: string): string | null;
  deleteElement(id: string): void;
}

let autosaveTimer: number | undefined;

function schedulePersistence(get: () => AppState): void {
  window.clearTimeout(autosaveTimer);
  const project = get().currentProject;
  if (project) void projectStore.saveRecovery(project).catch(() => undefined);
  autosaveTimer = window.setTimeout(() => void get().saveProject(false), 850);
}

export const useAppStore = create<AppState>((set, get) => ({
  route: 'home',
  projects: [],
  currentProject: null,
  history: [],
  future: [],
  loading: false,
  dirty: false,
  saveStatus: 'saved',
  saveError: null,
  notice: null,

  setRoute: (route) => {
    if (route === 'home') window.location.hash = '#/';
    set({ route });
  },
  clearNotice: () => set({ notice: null }),

  loadProjects: async () => {
    set({ loading: true });
    try {
      set({ projects: await projectStore.list(), loading: false });
    } catch (error) {
      set({ loading: false, notice: { kind: 'error', message: errorMessage(error) } });
    }
  },

  createProject: async (file) => {
    set({ loading: true });
    try {
      const project = await createProjectFromFile(file);
      set((state) => ({
        projects: [project, ...state.projects.filter((item) => item.id !== project.id)],
        currentProject: project,
        history: [],
        future: [],
        route: 'editor',
        loading: false,
        dirty: false,
        saveStatus: 'saved',
        saveError: null
      }));
      window.location.hash = `#/editor/${project.id}`;
    } catch (error) {
      set({ loading: false, notice: { kind: 'error', message: errorMessage(error) } });
    }
  },

  openIncomingFiles: async (files) => {
    const first = files[0];
    if (!first) return;
    if (first.name.toLowerCase().endsWith('.realimager')) {
      await get().importProject(first);
      return;
    }
    await get().createProject(first);
    for (const file of files.slice(1).filter((item) => !item.name.toLowerCase().endsWith('.realimager'))) await get().addImageElement(file);
  },

  openProject: async (id) => {
    if (get().currentProject?.id === id && get().route === 'editor') return;
    set({ loading: true });
    try {
      const saved = await projectStore.open(id);
      if (!saved) throw new Error('Project not found.');
      const recovered = await projectStore.recover(id);
      const project = recovered && recovered.updatedAt > saved.updatedAt ? recovered : saved;
      set({
        currentProject: project,
        history: [],
        future: [],
        route: 'editor',
        loading: false,
        dirty: Boolean(recovered && recovered.updatedAt > saved.updatedAt),
        saveStatus: recovered && recovered.updatedAt > saved.updatedAt ? 'saving' : 'saved',
        saveError: null,
        notice: recovered && recovered.updatedAt > saved.updatedAt
          ? { kind: 'info', message: 'Recovered your latest local changes.' }
          : null
      });
      window.location.hash = `#/editor/${project.id}`;
      if (recovered && recovered.updatedAt > saved.updatedAt) schedulePersistence(get);
    } catch (error) {
      set({ loading: false, notice: { kind: 'error', message: errorMessage(error) } });
    }
  },

  closeProject: async () => {
    window.clearTimeout(autosaveTimer);
    if (get().dirty) await get().saveProject(false);
    window.location.hash = '#/';
    set({ route: 'home', currentProject: null, history: [], future: [], dirty: false, saveStatus: 'saved' });
    await get().loadProjects();
  },

  updateProject: (updater, recordHistory = true) => {
    const current = get().currentProject;
    if (!current) return;
    const updated = updater(structuredClone(current));
    updated.updatedAt = new Date().toISOString();
    set((state) => ({
      currentProject: updated,
      dirty: true,
      saveStatus: 'saving',
      saveError: null,
      history: recordHistory ? [...state.history.slice(-99), current] : state.history,
      future: recordHistory ? [] : state.future
    }));
    schedulePersistence(get);
  },

  saveProject: async (showNotice = false) => {
    const project = get().currentProject;
    if (!project) return;
    set({ saveStatus: 'saving', saveError: null });
    try {
      let saved = project;
      try {
        saved = { ...project, thumbnailDataUrl: await refreshProjectThumbnail(project) };
      } catch {
        // A thumbnail failure must not block the editable workspace save.
      }
      await projectStore.save(saved);
      const current = get().currentProject;
      const unchanged = current?.updatedAt === project.updatedAt;
      set((state) => ({
        currentProject: unchanged ? saved : current,
        dirty: !unchanged,
        saveStatus: unchanged ? 'saved' : 'saving',
        projects: [saved, ...state.projects.filter((item) => item.id !== saved.id)],
        notice: showNotice ? { kind: 'success', message: 'Workspace saved locally.' } : state.notice
      }));
      if (!unchanged) schedulePersistence(get);
    } catch (error) {
      const message = errorMessage(error);
      set({ saveStatus: 'error', saveError: message, notice: { kind: 'error', message: `Could not save: ${message}` } });
    }
  },

  renameProject: async (id, name) => {
    if (!name.trim()) return;
    if (get().currentProject?.id === id) {
      get().updateProject((project) => ({ ...project, name: name.trim() }));
      return;
    }
    const project = await projectStore.open(id);
    if (!project) return;
    const updated = { ...project, name: name.trim(), updatedAt: new Date().toISOString() };
    await projectStore.save(updated);
    set((state) => ({ projects: state.projects.map((item) => item.id === id ? updated : item) }));
  },

  duplicateProject: async (id) => {
    const source = await projectStore.open(id);
    if (!source) return;
    const nextId = createId('project');
    const assets: ProjectAsset[] = [];
    const assetMap = new Map<string, string>();
    for (const asset of source.assets) {
      const blob = await projectStore.getAsset(asset.storageKey);
      const nextAssetId = createId('asset');
      const storageKey = `${nextId}/${nextAssetId}`;
      if (blob) await projectStore.putAsset(storageKey, blob);
      assets.push({ ...asset, id: nextAssetId, storageKey });
      assetMap.set(asset.id, nextAssetId);
    }
    const now = new Date().toISOString();
    const duplicate: RealImagerProject = {
      ...structuredClone(source),
      id: nextId,
      name: `${source.name} copy`,
      createdAt: now,
      updatedAt: now,
      assets,
      layers: source.layers.map((layer) => layer.type === 'raster'
        ? { ...layer, id: createId('layer'), assetId: assetMap.get(layer.assetId) ?? layer.assetId }
        : {
            ...layer,
            id: createId('layer'),
            elements: layer.elements.map((element) => ({
              ...element,
              id: createId('mark'),
              ...(element.type === 'image' ? { assetId: assetMap.get(element.assetId) ?? element.assetId } : {})
            }))
          })
    };
    await projectStore.save(duplicate);
    set((state) => ({ projects: [duplicate, ...state.projects], notice: { kind: 'success', message: 'Workspace duplicated.' } }));
  },

  deleteProject: async (id) => {
    await projectStore.remove(id);
    set((state) => ({ projects: state.projects.filter((item) => item.id !== id), notice: { kind: 'success', message: 'Workspace deleted.' } }));
  },

  importProject: async (file) => {
    set({ loading: true });
    try {
      const project = await projectStore.importArchive(file);
      set((state) => ({
        projects: [project, ...state.projects.filter((item) => item.id !== project.id)],
        currentProject: project,
        route: 'editor',
        loading: false,
        dirty: false,
        saveStatus: 'saved',
        history: [],
        future: []
      }));
      window.location.hash = `#/editor/${project.id}`;
    } catch (error) {
      set({ loading: false, notice: { kind: 'error', message: errorMessage(error) } });
    }
  },

  exportProject: async (id) => {
    const project = get().currentProject?.id === id ? get().currentProject : await projectStore.open(id);
    if (!project) throw new Error('Project not found.');
    return projectStore.exportArchive(project);
  },

  undo: () => {
    const { currentProject, history, future } = get();
    const previous = history.at(-1);
    if (!currentProject || !previous) return;
    const restored = { ...previous, updatedAt: new Date().toISOString() };
    set({ currentProject: restored, history: history.slice(0, -1), future: [currentProject, ...future], dirty: true, saveStatus: 'saving' });
    schedulePersistence(get);
  },

  redo: () => {
    const { currentProject, history, future } = get();
    const next = future[0];
    if (!currentProject || !next) return;
    const restored = { ...next, updatedAt: new Date().toISOString() };
    set({ currentProject: restored, history: [...history, currentProject], future: future.slice(1), dirty: true, saveStatus: 'saving' });
    schedulePersistence(get);
  },

  updateElement: (id, updater, recordHistory = true) => get().updateProject((project) => {
    const elements = getMarkupLayer(project).elements.map((element) => element.id === id ? updater(element) : element);
    return replaceMarkupElements(project, elements);
  }, recordHistory),

  addImageElement: async (file) => {
    const project = get().currentProject;
    if (!project) return null;
    try {
      const { assets, workingAsset, bitmap } = await createProjectAssets(file, project.id, 'imported-source');
      const scale = Math.min(
        project.canvas.width * .34 / Math.max(1, bitmap.width),
        project.canvas.height * .34 / Math.max(1, bitmap.height)
      );
      const width = bitmap.width * scale / project.canvas.width;
      const height = bitmap.height * scale / project.canvas.height;
      bitmap.close();
      const id = createId('mark');
      get().updateProject((current) => {
        const overlay = getMarkupLayer(current);
        const element: ImageElement = {
          id,
          type: 'image',
          name: file.name.replace(/\.[^.]+$/, ''),
          visible: true,
          locked: false,
          opacity: 1,
          rotation: 0,
          zIndex: overlay.elements.length,
          bounds: { x: 0.5 - width / 2, y: 0.5 - height / 2, width, height },
          assetId: workingAsset.id,
          crop: { x: 0, y: 0, width: 1, height: 1 }
        };
        return replaceMarkupElements({ ...current, assets: [...current.assets, ...assets] }, [...overlay.elements, element]);
      });
      return id;
    } catch (error) {
      set({ notice: { kind: 'error', message: errorMessage(error) } });
      return null;
    }
  },

  replaceImageElement: async (id, file) => {
    const project = get().currentProject;
    const existing = project && getMarkupLayer(project).elements.find((element) => element.id === id);
    if (!project || existing?.type !== 'image') return null;
    try {
      const { assets, workingAsset, bitmap } = await createProjectAssets(file, project.id, 'imported-source');
      const ratio = bitmap.width / Math.max(1, bitmap.height);
      bitmap.close();
      get().updateProject((current) => {
        const elements = getMarkupLayer(current).elements.map((element) => element.id === id && element.type === 'image'
          ? proportionalImageReplacement(element, ratio, current.canvas)
          : element);
        return replaceMarkupElements({ ...current, assets: [...current.assets, ...assets] }, elements.map((element) => element.id === id && element.type === 'image'
          ? { ...element, assetId: workingAsset.id, name: file.name.replace(/\.[^.]+$/, ''), crop: { x: 0, y: 0, width: 1, height: 1 } }
          : element));
      });
      return id;
    } catch (error) {
      set({ notice: { kind: 'error', message: errorMessage(error) } });
      return null;
    }
  },

  renameElement: (id, name) => {
    if (name.trim()) get().updateElement(id, (element) => ({ ...element, name: name.trim() }));
  },
  setElementVisibility: (id, visible) => get().updateElement(id, (element) => ({ ...element, visible })),
  setElementLocked: (id, locked) => get().updateElement(id, (element) => ({ ...element, locked })),
  moveElement: (id, direction) => get().updateProject((project) => reorderElements(project, id, direction === 1 ? 'forward' : 'backward')),
  reorderElement: (id, destination) => get().updateProject((project) => reorderElements(project, id, destination)),
  duplicateElement: (id) => {
    const project = get().currentProject;
    if (!project) return null;
    const source = getMarkupLayer(project).elements.find((element) => element.id === id);
    if (!source) return null;
    const nextId = createId('mark');
    get().updateProject((current) => {
      const overlay = getMarkupLayer(current);
      const duplicate = duplicateMarkupElement(source, overlay.elements.length);
      duplicate.id = nextId;
      return replaceMarkupElements(current, normalizeAutomaticNumbers([...overlay.elements, duplicate]));
    });
    return nextId;
  },
  deleteElement: (id) => get().updateProject((project) => replaceMarkupElements(
    project,
    normalizeAutomaticNumbers(getMarkupLayer(project).elements.filter((element) => element.id !== id).map((element, zIndex) => ({ ...element, zIndex })))
  ))
}));

function reorderElements(project: RealImagerProject, id: string, destination: 'front' | 'forward' | 'backward' | 'back'): RealImagerProject {
  const elements = [...getMarkupLayer(project).elements].sort((a, b) => a.zIndex - b.zIndex);
  const index = elements.findIndex((element) => element.id === id);
  if (index < 0) return project;
  const [element] = elements.splice(index, 1);
  const target = destination === 'front'
    ? elements.length
    : destination === 'back'
      ? 0
      : destination === 'forward'
        ? Math.min(elements.length, index + 1)
        : Math.max(0, index - 1);
  elements.splice(target, 0, element);
  return replaceMarkupElements(project, elements.map((item, zIndex) => ({ ...item, zIndex })));
}

function proportionalImageReplacement(element: ImageElement, ratio: number, canvas: RealImagerProject['canvas']): ImageElement {
  const currentWidth = element.bounds.width * canvas.width;
  const currentHeight = element.bounds.height * canvas.height;
  const area = Math.max(1, currentWidth * currentHeight);
  let width = Math.sqrt(area * ratio);
  let height = width / ratio;
  const scale = Math.min(1, canvas.width / width, canvas.height / height);
  width = width * scale / canvas.width;
  height = height * scale / canvas.height;
  const centerX = element.bounds.x + element.bounds.width / 2;
  const centerY = element.bounds.y + element.bounds.height / 2;
  return {
    ...element,
    bounds: {
      x: Math.max(0, Math.min(1 - width, centerX - width / 2)),
      y: Math.max(0, Math.min(1 - height, centerY - height / 2)),
      width,
      height
    }
  };
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}
